"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Mail, Phone, MapPin, Send, MessageSquare, Clock, CheckCircle } from "lucide-react"

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    project: "",
    message: "",
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    console.log("Form submitted:", formData)
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  return (
    <section id="contacto" className="py-20 bg-gray-50 relative overflow-hidden">
      {/* Elementos decorativos */}
      <div className="absolute top-20 left-10 w-40 h-40 bg-[#77c593]/8 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-20 w-60 h-60 bg-[#77c593]/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
            ¡Comencemos tu <span className="bg-gradient-to-r from-[#77c593] to-[#5a9d73] bg-clip-text text-transparent">Proyecto!</span>
          </h2>
          <div className="w-32 h-1.5 bg-gradient-to-r from-[#77c593] to-[#5a9d73] mx-auto mb-6 rounded-full"></div>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Contanos tu idea y te ayudamos a convertirla en una solución tecnológica que impulse tu negocio.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="flex items-center space-x-3 mb-8">
              <div className="bg-[#77c593]/15 p-3 rounded-xl">
                <MessageSquare size={24} className="text-[#77c593]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Hablemos</h3>
            </div>

            <div className="space-y-6 mb-8">
              {[
                { icon: Mail, title: "Email", info: "consultas@mayma.ar", desc: "Respondemos en 24hs" },
                { icon: Phone, title: "Teléfono", info: "+54 9 3446 21-4708", desc: "Lun-Vie 9:00-18:00" },
                { icon: MapPin, title: "Ubicación", info: "Gualeguaychú, Entre Ríos", desc: "Argentina" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ x: 5 }}
                  className="flex items-start space-x-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-[#77c593]/30 hover:shadow-md"
                >
                  <div className="bg-[#77c593]/10 p-3 rounded-lg">
                    <item.icon size={20} className="text-[#77c593]" />
                  </div>
                  <div>
                    <div className="text-gray-900 font-semibold">{item.title}</div>
                    <div className="text-[#77c593] font-medium">{item.info}</div>
                    <div className="text-gray-500 text-sm">{item.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* What to expect */}
            <div className="bg-gradient-to-br from-[#77c593]/5 to-[#77c593]/10 p-8 rounded-2xl border border-[#77c593]/20 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#77c593]/5 rounded-full blur-2xl"></div>
              <div className="relative z-10">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="bg-[#77c593] p-2 rounded-lg">
                    <Clock size={20} className="text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">¿Qué podés esperar?</h4>
                </div>
                <ul className="space-y-4 text-gray-700">
                  <li className="flex items-start space-x-3">
                    <CheckCircle size={18} className="text-[#77c593] flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-medium">Respuesta en 24hs</div>
                      <div className="text-sm text-gray-600">Te contactamos para entender tu proyecto</div>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle size={18} className="text-[#77c593] flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-medium">Propuesta detallada</div>
                      <div className="text-sm text-gray-600">Cronograma, costos y tecnologías</div>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle size={18} className="text-[#77c593] flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-medium">Desarrollo ágil</div>
                      <div className="text-sm text-gray-600">Entregas incrementales con feedback</div>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle size={18} className="text-[#77c593] flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-medium">Soporte continuo</div>
                      <div className="text-sm text-gray-600">Acompañamiento post-lanzamiento</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-2xl relative overflow-hidden">
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#77c593]/5 to-transparent rounded-full blur-2xl"></div>
              <div className="relative z-10">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="bg-gradient-to-br from-[#77c593] to-[#5a9d73] p-3 rounded-xl">
                    <MessageSquare size={24} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Contanos tu proyecto</h3>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
                    <input
                      type="text"
                      name="name"
                      placeholder="Tu nombre"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-4 text-gray-900 focus:border-[#77c593] focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#77c593]/20 transition-all duration-300"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="tu@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-4 text-gray-900 focus:border-[#77c593] focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#77c593]/20 transition-all duration-300"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Empresa</label>
                  <input
                    type="text"
                    name="company"
                    placeholder="Nombre de tu empresa"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-4 text-gray-900 focus:border-[#77c593] focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#77c593]/20 transition-all duration-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de proyecto</label>
                  <select
                    name="project"
                    value={formData.project}
                    onChange={handleChange}
                    className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-4 text-gray-900 focus:border-[#77c593] focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#77c593]/20 transition-all duration-300"
                    required
                  >
                    <option value="">Selecciona una opción</option>
                    <option value="web">Desarrollo Web</option>
                    <option value="mobile">Aplicación Móvil</option>
                    <option value="desktop">Aplicación de escritorio</option>
                    <option value="sistema">Sistema Integral</option>
                    <option value="automation">Automatización</option>
                    <option value="consulting">Consultoría Técnica</option>
                    <option value="other">Otro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contanos un poco mas </label>
                  <textarea
                    name="message"
                    placeholder="Describi tu proyecto, los inconvenientes actuales o qué te gustaría hacer..."
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="w-full bg-gray-50 border border-gray-300 rounded-xl px-4 py-4 text-gray-900 focus:border-[#77c593] focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#77c593]/20 transition-all duration-300 resize-none"
                    required
                  />
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02, boxShadow: "0 15px 35px rgba(119, 197, 147, 0.4)" }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-[#77c593] to-[#5a9d73] text-white px-8 py-4 rounded-xl font-semibold flex items-center justify-center space-x-2 hover:from-[#5a9d73] hover:to-[#77c593] shadow-xl transition-all duration-300"
                >
                  <span>Enviar Mensaje</span>
                  <Send size={20} />
                </motion.button>
                
                <p className="text-sm text-gray-500 text-center mt-4">
                  Al enviar tu mensaje aceptás que nos pongamos en contacto contigo para brindarte más información.
                </p>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default Contact
